import React from 'react';
import { Item, Patient } from '../../types/api';
import { OrderItem } from '../../types/dispense';

interface ItemSelectionModalProps {
  show: boolean;
  showOrderPanel: boolean;
  itemSearch: string;
  filteredItems: Item[];
  orders: OrderItem[];
  totalAmount: number;
  patientAllergies: string[];
  itemFilter: string;
  setShow: (show: boolean) => void;
  setItemSearch: (search: string) => void;
  setItemFilter: (filter: string) => void;
  onSearchChange: (value: string, filter: string) => Promise<void>;
  onAddItem: (item: Item) => void;
}

export function ItemSelectionModal({
  show, showOrderPanel, itemSearch, filteredItems, orders, totalAmount,
  patientAllergies, itemFilter,
  setShow, setItemSearch, setItemFilter, onSearchChange, onAddItem
}: ItemSelectionModalProps) {
  if (!show) return null;

  return (
    <div
      className="modal-overlay"
      onClick={() => setShow(false)}
      style={{ zIndex: showOrderPanel ? 1100 : 1000 }}
    >
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Add Items</div>
          <button className="modal-close" onClick={() => setShow(false)}>&times;</button>
        </div>

        <div className="item-search-bar">
          <input
            type="text"
            placeholder="Search medications, procedures, lab tests..."
            value={itemSearch}
            onChange={async (e) => {
              setItemSearch(e.target.value);
              if (e.target.value.length >= 2 || e.target.value.length === 0) {
                await onSearchChange(e.target.value, itemFilter);
              }
            }}
            autoFocus
          />
        </div>

        <div className="item-filter-tabs">
          {[{ key: 'ALL', label: 'All' }, { key: 'MED', label: 'Medications' }, { key: 'PRC', label: 'Procedures' }, { key: 'RAD', label: 'Radiological' }].map(f => (
            <button
              key={f.key}
              className={`filter-tab ${itemFilter === f.key ? 'active' : ''}`}
              onClick={async () => {
                setItemFilter(f.key);
                await onSearchChange(itemSearch, f.key);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="item-list">
          {filteredItems.map(item => {
            const isAdded = orders.some(o => o.id === item.id);
            const isAllergy = patientAllergies.some(a => item.itemName.toLowerCase().includes(a.toLowerCase()));
            return (
              <div
                key={item.id}
                className={`item-row ${isAdded ? 'added' : ''} ${isAllergy ? 'allergy-warning' : ''}`}
                onClick={() => onAddItem(item)}
              >
                <div className="item-info">
                  <div className="item-name">
                    {item.itemName}
                    {isAllergy && <span className="allergy-tag">ALLERGY</span>}
                  </div>
                  <div className="item-meta">
                    {item.itemCode} • {item.itemType === 'MED' ? 'Medication' : item.itemType === 'PRC' ? 'Procedure' : 'Lab Test'}
                    {item.itemType === 'MED' && item.defaultDose && item.defaultFrequency && ` • ${item.defaultDose} ${item.defaultFrequency}`}
                  </div>
                </div>
                <div className="item-price">${item.price.toFixed(2)}</div>
                {isAdded ? (
                  <span className="added-badge">Added</span>
                ) : (
                  <button className="add-btn">+</button>
                )}
              </div>
            );
          })}
        </div>

        <div className="modal-footer">
          <div className="items-count">{orders.length} items • ${totalAmount.toFixed(2)}</div>
          <button className="btn btn-primary" onClick={() => setShow(false)}>Done</button>
        </div>
      </div>
    </div>
  );
}
